import { memo } from 'react';
import { Chip, Tooltip, Typography } from '@mui/material';
import { Scale } from '@mui/icons-material';
import { RATING_SCALE_LABEL } from '@/components/campaigns/CampaignQuestionsEditor/constants';

import { CampaignQuestion, QuestionType } from '../CampaignQuestionsEditor/types';

interface QuestionValueBadgeProps {
    question: CampaignQuestion;
}

const TOOLTIP_TEXTS = {
    boolean: 'Scored as: false - 0, true - 1.',
    ratingScale: 'Scored using the rating scale:',
    minMax: (min: number, max: number) => `Scored using values from ${min} to ${max}.`,
} as const;

/**
 * Displays a badge showing question value range with tooltip information.
 *
 * @param {QuestionValueBadgeProps} props
 * @returns {JSX.Element | null}
 */
const QuestionValueBadge: React.FC<QuestionValueBadgeProps> = ({ question }) => {
    const getBadgeAndTooltip = () => {
        if (question.type === QuestionType.BOOLEAN) {
            return {
                badge: '0/1',
                tooltip: TOOLTIP_TEXTS.boolean,
            };
        }

        if (question.type === QuestionType.NUMERIC) {
            if (question.useRatingScale) {
                return {
                    badge: <Scale sx={{ fontSize: '0.75rem' }} />,
                    tooltip: `${TOOLTIP_TEXTS.ratingScale} ${RATING_SCALE_LABEL}`,
                };
            }

            return {
                badge: `${question.minValue}-${question.maxValue}`,
                tooltip: TOOLTIP_TEXTS.minMax(question.minValue, question.maxValue),
            };
        }

        return null;
    };

    const content = getBadgeAndTooltip();

    if (!content) {
        return null;
    }

    return (
        <Tooltip title={<Typography variant="body2">{content.tooltip}</Typography>} arrow placement="top">
            <Chip
                label={content.badge}
                size="small"
                sx={{
                    height: '20px',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    cursor: 'help',
                    '& .MuiChip-label': {
                        px: 0.75,
                        py: 0,
                        display: 'flex',
                        alignItems: 'center',
                    },
                }}
            />
        </Tooltip>
    );
};

export default memo(QuestionValueBadge);
